import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { projectsAPI, invitationsAPI } from '../services/api'
import { X, Briefcase, Euro, Calendar, Send, CheckCircle, AlertCircle, Plus } from 'lucide-react'

const getProjectId = (project) => project._id || project.id

const InviteCreatorModal = ({ isOpen, onClose, creatorId, creatorName }) => {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedProject, setSelectedProject] = useState(null)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  // Body-scroll-lock zolang de modal open is (voorkomt scroll chaining naar de pagina eronder).
  useEffect(() => {
    if (!isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setMessage('')
      setSuccess(false)
      setError('')
      setLoading(true)
      setProjects([])
      setSelectedProject(null)
      projectsAPI.getMyProjects({ status: 'open' })
        .then(response => {
          const allProjects = response.data.projects || response.data || []
          const openProjects = Array.isArray(allProjects)
            ? allProjects.filter(p => p.status === 'open')
            : []
          setProjects(openProjects)
          // Auto-select first project
          if (openProjects.length > 0) {
            setSelectedProject(getProjectId(openProjects[0]))
          }
        })
        .catch(err => {
          console.error('Error fetching projects:', err)
          setError('Kon opdrachten niet laden')
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [isOpen])

  const handleSelectProject = (projectId) => {
    setSelectedProject(projectId)
    setError('')
  }

  const handleSend = async () => {
    // Fallback: if nothing selected but projects exist, use first one
    const projectId = selectedProject || (projects.length > 0 ? getProjectId(projects[0]) : null)

    if (!projectId) {
      setError('Selecteer een opdracht')
      return
    }

    try {
      setSending(true)
      setError('')
      await invitationsAPI.create({
        projectId,
        creatorId,
        message
      })
      setSuccess(true)
    } catch (err) {
      const msg = err.response?.data?.error || 'Er is een fout opgetreden bij het versturen'
      setError(msg)
    } finally {
      setSending(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal: full-screen op mobiel, gecentreerde kaart vanaf sm; body scrolt intern, footer vast */}
      <div className="relative w-full max-w-lg bg-white rounded-none sm:rounded-2xl shadow-2xl h-full sm:h-auto max-h-[100dvh] sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-[#0F172A]">Stel opdracht voor</h2>
            <p className="text-sm text-[#475569] mt-1 break-words">
              Nodig {creatorName} uit voor een opdracht
            </p>
          </div>
          <button onClick={onClose} className="p-2.5 hover:bg-gray-100 rounded-lg transition flex-shrink-0" aria-label="Sluiten">
            <X className="w-5 h-5 text-[#475569]" />
          </button>
        </div>

        <div className="p-4 sm:p-6 flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {/* Success state */}
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-bold mb-2">Uitnodiging verstuurd!</h3>
              <p className="text-[#475569] text-sm mb-6">
                {creatorName} ontvangt een melding en kan reageren.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-[#3EB489] text-white rounded-lg font-medium hover:bg-[#359E78] transition"
              >
                Sluiten
              </button>
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-[#1D8BF8] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#475569] text-sm">Opdrachten laden...</p>
            </div>
          ) : projects.length === 0 ? (
            /* No open projects */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-[#1D8BF8]" />
              </div>
              <h3 className="text-lg font-bold mb-2">Geen open opdrachten</h3>
              <p className="text-[#475569] text-sm mb-6">
                Je hebt nog geen open opdrachten. Maak eerst een opdracht aan.
              </p>
              <Link
                to="/projects/create"
                onClick={onClose}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3EB489] text-white rounded-lg font-medium hover:bg-[#359E78] transition"
              >
                <Plus className="w-5 h-5" />
                Maak nieuwe opdracht
              </Link>
            </div>
          ) : (
            <>
              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Project selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#0F172A] mb-2">
                  Selecteer een opdracht
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto overscroll-contain">
                  {projects.map(project => {
                    const pid = getProjectId(project)
                    const isSelected = selectedProject === pid
                    return (
                      <div
                        key={pid}
                        onClick={() => handleSelectProject(pid)}
                        className={`block p-4 rounded-xl border-2 cursor-pointer transition ${
                          isSelected
                            ? 'border-[#1D8BF8] bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-[#0F172A] break-words">{project.title}</h4>
                            <div className="flex items-center gap-3 mt-1 text-xs text-[#475569]">
                              {project.budget && (
                                <span className="flex items-center gap-1">
                                  <Euro className="w-3 h-3" />
                                  €{project.budget}
                                </span>
                              )}
                              {project.deadline && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(project.deadline).toLocaleDateString('nl-NL')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isSelected
                              ? 'border-[#1D8BF8] bg-[#3EB489]'
                              : 'border-gray-300'
                          }`}>
                            {isSelected && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Optional message */}
              <div className="mb-2 sm:mb-0">
                <label className="block text-sm font-medium text-[#0F172A] mb-2">
                  Bericht (optioneel)
                </label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Vertel de creator waarom je hem/haar wilt uitnodigen..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#1D8BF8]/20 focus:border-[#1D8BF8] resize-none"
                />
                <p className="text-xs text-[#475569] mt-1 text-right">
                  {message.length}/500
                </p>
              </div>
            </>
          )}
        </div>

        {/* Vaste footer met verzendknop (alleen in de formulier-staat) */}
        {!success && !loading && projects.length > 0 && (
          <div className="flex-shrink-0 p-4 sm:px-6 sm:pb-6 sm:pt-0 border-t border-gray-100 sm:border-t-0">
            <button
              onClick={handleSend}
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#3EB489] text-white rounded-xl font-semibold hover:bg-[#359E78] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
              {sending ? 'Versturen...' : 'Uitnodiging versturen'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default InviteCreatorModal
